import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../shared/ui/kit/utils';
import type { MessageStats } from '../types';

interface MessagesHeaderProps {
    stats: MessageStats;
    onMarkAllAsRead: () => void;
    onRefresh: () => void;
    isMarkingAllAsRead: boolean;
    isRefreshing: boolean;
}

export const MessagesHeader: React.FC<MessagesHeaderProps> = ({
    stats,
    onMarkAllAsRead,
    onRefresh,
    isMarkingAllAsRead,
    isRefreshing,
}) => {
    const hasUnread = stats.unread > 0;

    return (
        <header className="px-[18px] pt-4 pb-2.5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <motion.h1 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-[17px] font-semibold tracking-[-0.3px] text-gray-900 dark:text-gray-100"
                    >
                        Уведомления
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5"
                    >
                        {stats.unread > 0 ? (
                            <span className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                                {stats.unread} непрочитанных · {stats.critical > 0 && `${stats.critical} критичных`}
                            </span>
                        ) : (
                            'Всё прочитано · вы в курсе всех событий'
                        )}
                    </motion.p>
                </div>

                <div className="flex items-center gap-2">
                    {hasUnread && (
                        <motion.button
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={onMarkAllAsRead}
                            disabled={isMarkingAllAsRead}
                            className={cn(
                                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium',
                                'bg-white dark:bg-white/5',
                                'border border-black/[.07] dark:border-white/[.07]',
                                'text-gray-700 dark:text-gray-300',
                                'hover:bg-gray-50 dark:hover:bg-white/10',
                                'disabled:opacity-50 disabled:cursor-not-allowed',
                                'transition-all'
                            )}
                        >
                            {isMarkingAllAsRead ? (
                                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                                </svg>
                            ) : (
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path d="M18 6L6 18M6 6l12 12"/>
                                </svg>
                            )}
                            Все прочитано
                        </motion.button>
                    )}

                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={onRefresh}
                        disabled={isRefreshing}
                        className={cn(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium',
                            'bg-white dark:bg-white/5',
                            'border border-black/[.07] dark:border-white/[.07]',
                            'text-gray-500 dark:text-gray-400',
                            'hover:bg-gray-50 dark:hover:bg-white/10',
                            'disabled:opacity-50',
                            'transition-all'
                        )}
                    >
                        <svg className={cn(isRefreshing && 'animate-spin')} width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <polyline points="23 4 23 10 17 10"/>
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                        </svg>
                        <span className="hidden sm:inline">Обновить</span>
                    </motion.button>
                </div>
            </div>
        </header>
    );
};